import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface Statistics {
  total_investments: number
  active_investments: number
  total_invested: number
  avg_roi: number
  payback_period_avg_months: number
  irr_avg: number
}

interface Investment {
  id: string
  name: string
  amount: number
  roi: number
  payback_months: number
  status: string
}

interface ROIBreakdown {
  category: string
  invested: number
  returns: number
  roi: number
}

export default function InvestmentAnalysisPage() {
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [investments, setInvestments] = useState<Investment[]>([])
  const [roiBreakdown, setRoiBreakdown] = useState<ROIBreakdown[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, invRes, roiRes] = await Promise.all([
          fetch('/api/investmentanalysis/statistics'),
          fetch('/api/investmentanalysis/investments'),
          fetch('/api/investmentanalysis/roibreakdown')
        ])
        setStatistics(await statsRes.json())
        setInvestments(await invRes.json())
        setRoiBreakdown(await roiRes.json())
      } catch (error) {
        console.error('Error fetching data:', error)
      } finally {
        setLoading(false)
      }
    }
    fetchData()
  }, [])

  const getStatusBadge = (status: string) => {
    const colors: Record<string, string> = {
      'Performing': 'bg-green-100 text-green-800',
      'On Track': 'bg-blue-100 text-blue-800',
      'Exceeding': 'bg-purple-100 text-purple-800',
      'Monitoring': 'bg-yellow-100 text-yellow-800'
    }
    return colors[status] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return <div className="p-8"><Header /><div className="mt-8">Loading Investment Analysis...</div></div>
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <div className="p-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-8">Investment Analysis</h1>

        {statistics && (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-blue-600">{statistics.total_investments}</div>
              <div className="text-sm text-gray-500">Investments</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-green-600">{statistics.active_investments}</div>
              <div className="text-sm text-gray-500">Active</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-purple-600">${(statistics.total_invested / 1000000).toFixed(0)}M</div>
              <div className="text-sm text-gray-500">Total Invested</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-indigo-600">{statistics.avg_roi}%</div>
              <div className="text-sm text-gray-500">Avg ROI</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-orange-600">{statistics.payback_period_avg_months}mo</div>
              <div className="text-sm text-gray-500">Avg Payback</div>
            </div>
            <div className="bg-white p-4 rounded-lg shadow">
              <div className="text-2xl font-bold text-teal-600">{statistics.irr_avg}%</div>
              <div className="text-sm text-gray-500">Avg IRR</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">Investment Portfolio</h2></div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Investment</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Amount</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">ROI</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {investments.map((inv) => (
                    <tr key={inv.id} className="hover:bg-gray-50">
                      <td className="px-4 py-3 font-medium">{inv.name}</td>
                      <td className="px-4 py-3 text-gray-600">${(inv.amount / 1000000).toFixed(1)}M</td>
                      <td className="px-4 py-3 text-green-600">{inv.roi}%</td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded-full text-xs ${getStatusBadge(inv.status)}`}>
                          {inv.status}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="p-4 border-b"><h2 className="text-lg font-semibold">ROI by Category</h2></div>
            <div className="p-4 space-y-4">
              {roiBreakdown.map((r, idx) => (
                <div key={idx} className="p-3 bg-gray-50 rounded-lg">
                  <div className="flex justify-between items-center">
                    <span className="font-medium">{r.category}</span>
                    <span className="text-green-600 font-semibold">{r.roi}% ROI</span>
                  </div>
                  <div className="grid grid-cols-2 gap-4 mt-2 text-sm">
                    <div>
                      <span className="text-gray-500">Invested: </span>
                      <span className="font-medium">${(r.invested / 1000000).toFixed(1)}M</span>
                    </div>
                    <div>
                      <span className="text-gray-500">Returns: </span>
                      <span className="font-medium text-green-600">${(r.returns / 1000000).toFixed(1)}M</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
